import { AgentChatInput } from '@/components/AgentChat/AgentChatInput';
import { useUserContext } from '@/contexts/UserContext';
import { PromptAttachment } from '@/types/agentChat';
import { AgentRunAttachment, VariantPrompt } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import { Dialog, DialogContent } from '@superfunnel/ui-core/v2/Dialog';
import { ArrowLeft, ArrowRight, Xmark } from 'iconoir-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DynamicPageList } from './DynamicPageList';
import { OriginSelection } from './OriginSelection';
import {
  ControlVariationInfo,
  DynamicPageWithVariants,
  VariantInfo,
  VariantOriginModalMode,
  VariantOriginResult,
  VariantOriginType,
  VariantSelection,
} from './types';
import { VariantList } from './VariantList';

export interface VariantOriginModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
  /** Callback when a selection is made */
  onSelect: (result: VariantOriginResult) => void;
  /** Mode: variant or test */
  mode?: VariantOriginModalMode;
  /** Title for the modal (e.g., "Generate variant") */
  title?: string;
  /** Subtitle for the modal (e.g., "For Women Audience") */
  subtitle?: string;
  /** Variants in the current dynamic page (for 'existingVariant' mode) */
  currentPageVariants?: VariantInfo[];
  /** Current dynamic page ID */
  currentDynamicPageId?: string;
  /** Current dynamic page name */
  currentDynamicPageName?: string;
  /** All dynamic pages with their variants (for 'differentDynamicPage' mode) */
  allDynamicPages?: DynamicPageWithVariants[];
  /** The ID of the current variant to exclude from the list */
  excludeVariantId?: string;
  /** The ID of the current dynamic page to exclude from the list */
  excludeDynamicPageId?: string;
  /** Whether actions are disabled (e.g., loading state) */
  disabled?: boolean;
  /** Control variation info for test mode (first websiteId of the variant) */
  controlVariation?: ControlVariationInfo;
  /** Default prompt to prefill the input (from variant's stored prompt) */
  defaultPrompt?: VariantPrompt;
}

type ModalStep = 'selection' | VariantOriginType;

export default function VariantOriginModal({
  open,
  onClose,
  onSelect,
  mode = 'variant',
  title = 'Generate variant',
  subtitle,
  currentPageVariants = [],
  currentDynamicPageId = '',
  currentDynamicPageName = '',
  allDynamicPages = [],
  excludeVariantId,
  excludeDynamicPageId,
  disabled,
  controlVariation,
  defaultPrompt,
}: VariantOriginModalProps) {
  const { selectedAccount } = useUserContext();
  const [step, setStep] = useState<ModalStep>('selection');
  const [selectedVariant, setSelectedVariant] = useState<
    VariantSelection | undefined
  >();
  // Ref to store current prompt/attachments for variant selection auto-submit
  const currentInputRef = useRef<{
    prompt: string;
    attachments: AgentRunAttachment[];
  }>({ prompt: '', attachments: [] });

  // Local prompt attachment state for AgentChatInput
  const [promptContexts, setPromptContexts] = useState<PromptAttachment[]>([]);

  const updatePromptContext = useCallback(
    (id: string, updates: Partial<PromptAttachment>) => {
      setPromptContexts((prev) =>
        prev.map((ctx) =>
          ctx.id === id ? ({ ...ctx, ...updates } as PromptAttachment) : ctx,
        ),
      );
    },
    [],
  );

  const addPromptContext = useCallback((context: PromptAttachment) => {
    setPromptContexts((prev) => [...prev, context]);
  }, []);

  // Filter out draft variants and the current variant from the current page variants
  const filteredCurrentPageVariants = useMemo(() => {
    return currentPageVariants.filter((v) => {
      // Exclude draft variants
      if (v.status === 'draft') return false;
      // Exclude the current variant
      if (excludeVariantId && v.id === excludeVariantId) return false;
      return true;
    });
  }, [currentPageVariants, excludeVariantId]);

  // Filter dynamic pages: exclude current page, exclude draft pages, filter out pages with only draft variants
  const filteredDynamicPages = useMemo(() => {
    return allDynamicPages
      .filter((dp) => {
        // Exclude the current dynamic page
        if (
          excludeDynamicPageId &&
          dp.dynamicPage.id === excludeDynamicPageId
        ) {
          return false;
        }
        // Exclude draft dynamic pages
        if (dp.dynamicPage.data().status === 'draft') {
          return false;
        }
        // Check if the page has at least one non-draft variant
        const hasNonDraftVariant = dp.variants.some(
          (v) => v.data().status !== 'draft',
        );
        return hasNonDraftVariant;
      })
      .map((dp) => ({
        ...dp,
        // Filter out draft variants from each page
        variants: dp.variants.filter((v) => v.data().status !== 'draft'),
      }));
  }, [allDynamicPages, excludeDynamicPageId]);

  // Determine which options to show
  const showExistingVariantOption = filteredCurrentPageVariants.length > 0;
  const showDifferentPageOption = filteredDynamicPages.length > 0;

  // Memoize the default text value for AgentChatInput
  const defaultPromptText = useMemo(
    () => defaultPrompt?.text || '',
    [defaultPrompt],
  );

  // Helper to convert default attachments to prompt attachments
  const initializeFromDefaultPrompt = useCallback(() => {
    if (!defaultPrompt) return;

    // Initialize ref with default values
    currentInputRef.current = {
      prompt: defaultPrompt.text || '',
      attachments: defaultPrompt.attachments || [],
    };

    // Convert file and asset attachments to prompt attachments
    // These are already stored as AgentRunAttachment, so we can use them directly
    // For file type, we add the uploadState to indicate it's already uploaded
    const attachments = defaultPrompt.attachments || [];
    if (attachments.length > 0) {
      const promptAttachments: PromptAttachment[] = attachments.map(
        (attachment) => {
          if (attachment.type === 'file') {
            return {
              ...attachment,
              // Mark as already uploaded
              uploadState: {
                isUploading: false,
                progress: 100,
                uploadedUrl: attachment.url,
              },
            };
          }
          // For other types (asset, campaign, element), use directly
          return attachment;
        },
      );
      setPromptContexts(promptAttachments);
    }
  }, [defaultPrompt]);

  // Initialize from default prompt when modal opens
  useEffect(() => {
    if (open && defaultPrompt) {
      initializeFromDefaultPrompt();
    }
  }, [open, defaultPrompt, initializeFromDefaultPrompt]);

  // Helper to reset internal state
  const resetState = useCallback(() => {
    setStep('selection');
    setSelectedVariant(undefined);
    currentInputRef.current = { prompt: '', attachments: [] };
    setPromptContexts([]);
  }, []);

  // Reset state when modal closes (controlled by parent)
  useEffect(() => {
    if (!open) {
      // Delay reset to allow close animation
      const timer = setTimeout(resetState, 200);
      return () => clearTimeout(timer);
    }
  }, [open, resetState]);

  // Reset state when modal opens/closes via Dialog
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
        // State reset is handled by useEffect above
      }
    },
    [onClose],
  );

  const handleBack = useCallback(() => {
    resetState();
  }, [resetState]);

  const handleOriginSelect = useCallback((type: VariantOriginType) => {
    setStep(type);
  }, []);

  const handleContinueWithoutPrompt = useCallback(() => {
    // Use current attachments from ref (user may have added files without a prompt)
    const { attachments: currentAttachments } = currentInputRef.current;
    const resultAttachments =
      currentAttachments.length > 0 ? currentAttachments : undefined;

    if (step === 'empty') {
      onSelect({
        originType: 'empty',
        attachments: resultAttachments,
      });
    } else if (step === 'controlVariation' && controlVariation) {
      onSelect({
        originType: 'controlVariation',
        attachments: resultAttachments,
        selectedVariant: {
          variantId: controlVariation.variantId,
          variantName: controlVariation.variantName,
          websiteId: controlVariation.websiteId,
          dynamicPageId: currentDynamicPageId,
          dynamicPageName: currentDynamicPageName,
        },
      });
    }
  }, [
    step,
    controlVariation,
    currentDynamicPageId,
    currentDynamicPageName,
    onSelect,
  ]);

  const handleVariantSelect = useCallback(
    (selection: VariantSelection) => {
      setSelectedVariant(selection);
      // Auto-submit when selecting a variant, using ref for current input values
      const { prompt: currentPrompt, attachments: currentAttachments } =
        currentInputRef.current;
      onSelect({
        originType: step as 'existingVariant' | 'differentDynamicPage',
        prompt: currentPrompt.trim() || undefined,
        attachments:
          currentAttachments.length > 0 ? currentAttachments : undefined,
        selectedVariant: selection,
      });
    },
    [step, onSelect],
  );

  const handleDynamicPageVariantSelect = useCallback(
    (selection: VariantSelection) => {
      setSelectedVariant(selection);
      // Auto-submit when selecting a variant, using ref for current input values
      const { prompt: currentPrompt, attachments: currentAttachments } =
        currentInputRef.current;
      onSelect({
        originType: 'differentDynamicPage',
        prompt: currentPrompt.trim() || undefined,
        attachments:
          currentAttachments.length > 0 ? currentAttachments : undefined,
        selectedVariant: selection,
      });
    },
    [onSelect],
  );

  const getStepTitle = useCallback(() => {
    switch (step) {
      case 'empty':
        return `${title} / Start from scratch`;
      case 'controlVariation':
        return `${title} / Start from control`;
      case 'existingVariant':
        return `${title} / Copy from a variant`;
      case 'differentDynamicPage':
        return `${title} / Copy from another page`;
      default:
        return title;
    }
  }, [step, title]);

  // Handler for AgentChatInput - captures message and attachments
  const handleSendMessage = useCallback(
    (message: string, messageAttachments?: AgentRunAttachment[]) => {
      // Store in ref for variant selection auto-submit
      currentInputRef.current = {
        prompt: message,
        attachments: messageAttachments || [],
      };

      // For steps that don't require variant selection, submit immediately
      if (step === 'empty' || step === 'controlVariation') {
        const trimmedPrompt = message.trim() || undefined;
        const resultAttachments =
          messageAttachments && messageAttachments.length > 0
            ? messageAttachments
            : undefined;

        if (step === 'empty') {
          onSelect({
            originType: 'empty',
            prompt: trimmedPrompt,
            attachments: resultAttachments,
          });
        } else if (step === 'controlVariation' && controlVariation) {
          onSelect({
            originType: 'controlVariation',
            prompt: trimmedPrompt,
            attachments: resultAttachments,
            selectedVariant: {
              variantId: controlVariation.variantId,
              variantName: controlVariation.variantName,
              websiteId: controlVariation.websiteId,
              dynamicPageId: currentDynamicPageId,
              dynamicPageName: currentDynamicPageName,
            },
          });
        }
      }
    },
    [
      step,
      controlVariation,
      currentDynamicPageId,
      currentDynamicPageName,
      onSelect,
    ],
  );

  const isSubStep = step !== 'selection';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex w-full max-w-[500px] flex-col gap-5 rounded-2xl bg-white p-6"
      >
        {/* Header */}
        <div className="flex items-start justify-between self-stretch">
          {isSubStep ? (
            <div className="flex items-center gap-4">
              {/* Back button */}
              <button
                type="button"
                onClick={handleBack}
                className="hover:bg-base-100 rounded p-1 transition-colors"
                disabled={disabled}
              >
                <ArrowLeft className="text-base-900 size-5" strokeWidth={1.2} />
              </button>
              {/* Separator */}
              <div className="h-[34px] w-px bg-[#e4e7ec]" />
              {/* Title */}
              <div className="flex grow flex-col justify-center gap-1 px-1">
                <p className="text-base-900 text-base font-medium">
                  {getStepTitle()}
                </p>
                {subtitle && (
                  <p className="text-base-500 text-xs">{subtitle}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex w-full flex-col items-center justify-center gap-1.5">
              <p className="text-base-900 w-full max-w-[432px] text-center text-lg font-medium">
                {title}
              </p>
              {subtitle && <p className="text-base-500 text-xs">{subtitle}</p>}
            </div>
          )}
          {/* Close button - only show on initial screen */}
          {!isSubStep && (
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="hover:bg-base-100 rounded p-1 transition-colors"
            >
              <Xmark className="text-base-900 size-5" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col gap-3 self-stretch">
          {step === 'selection' && (
            <OriginSelection
              onSelect={handleOriginSelect}
              mode={mode}
              showExistingVariantOption={showExistingVariantOption}
              showDifferentPageOption={showDifferentPageOption}
            />
          )}

          {step === 'empty' && (
            <div className="flex flex-col gap-2.5">
              <AgentChatInput
                onSendMessage={handleSendMessage}
                variant="outline"
                size="sm"
                disabled={disabled}
                placeholder="Enter an optional prompt for the builder…"
                contextTypes={[{ type: 'file', location: 'dropdown' }]}
                promptContexts={promptContexts}
                setPromptContexts={setPromptContexts}
                updatePromptContext={updatePromptContext}
                addPromptContext={addPromptContext}
                defaultValue={defaultPromptText}
                maxLines={12}
              />
              <Button
                onClick={handleContinueWithoutPrompt}
                variant="link"
                size="sm"
                disabled={disabled}
                className="w-full"
              >
                Continue without prompt
                <ArrowRight className="size-3" strokeWidth={1.2} />
              </Button>
            </div>
          )}

          {step === 'controlVariation' && (
            <div className="flex flex-col gap-2.5">
              <AgentChatInput
                onSendMessage={handleSendMessage}
                size="sm"
                variant="outline"
                disabled={disabled}
                placeholder="Enter an optional prompt for the builder…"
                contextTypes={[{ type: 'file', location: 'dropdown' }]}
                promptContexts={promptContexts}
                setPromptContexts={setPromptContexts}
                updatePromptContext={updatePromptContext}
                addPromptContext={addPromptContext}
                defaultValue={defaultPromptText}
                maxLines={12}
              />
              <Button
                onClick={handleContinueWithoutPrompt}
                variant="link"
                size="sm"
                disabled={disabled}
                className="w-full"
              >
                Continue without prompt
                <ArrowRight className="size-3" strokeWidth={1.2} />
              </Button>
            </div>
          )}

          {step === 'existingVariant' && (
            <>
              <AgentChatInput
                onSendMessage={handleSendMessage}
                size="sm"
                variant="outline"
                disabled={disabled}
                placeholder="Enter an optional prompt for the builder…"
                contextTypes={[{ type: 'file', location: 'dropdown' }]}
                promptContexts={promptContexts}
                setPromptContexts={setPromptContexts}
                updatePromptContext={updatePromptContext}
                addPromptContext={addPromptContext}
                defaultValue={defaultPromptText}
                maxLines={12}
              />
              {/* Divider */}
              <div className="flex items-center justify-center gap-4 self-stretch">
                <div className="h-px grow bg-[#e4e7ec]" />
                <p className="text-base-700 text-center text-xs">
                  and select a variant
                </p>
                <div className="h-px grow bg-[#e4e7ec]" />
              </div>
              {/* Variant list */}
              <VariantList
                variants={filteredCurrentPageVariants}
                selectedVariant={selectedVariant}
                dynamicPageId={currentDynamicPageId}
                dynamicPageName={currentDynamicPageName}
                onSelect={handleVariantSelect}
                accountId={selectedAccount?.id || ''}
              />
            </>
          )}

          {step === 'differentDynamicPage' && (
            <>
              <AgentChatInput
                onSendMessage={handleSendMessage}
                size="sm"
                variant="outline"
                disabled={disabled}
                placeholder="Enter an optional prompt for the builder…"
                contextTypes={[{ type: 'file', location: 'dropdown' }]}
                promptContexts={promptContexts}
                setPromptContexts={setPromptContexts}
                updatePromptContext={updatePromptContext}
                addPromptContext={addPromptContext}
                defaultValue={defaultPromptText}
                maxLines={12}
              />
              {/* Divider */}
              <div className="flex items-center justify-center gap-4 self-stretch">
                <div className="h-px grow bg-[#e4e7ec]" />
                <p className="text-base-700 text-center text-xs">
                  and select a variant
                </p>
                <div className="h-px grow bg-[#e4e7ec]" />
              </div>
              {/* Dynamic page list */}
              <DynamicPageList
                dynamicPages={filteredDynamicPages}
                selectedVariant={selectedVariant}
                onSelect={handleDynamicPageVariantSelect}
                accountId={selectedAccount?.id || ''}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
